"use client";

import { cn } from "@/lib/utils";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { PageChallengeSchema } from "@/sanity/pages/PageChallengeTemplate";
import ChipSerif from "@/components/global/ui/ChipSerif";
import checkbox from "@/public/challenge/checkbox.svg";
import { motion } from "framer-motion";
import FAQList from "@/components/global/ui/FAQList";
import { PortableTextBlock } from "sanity";
import { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import iqicon from "@/public/affiliate/iqIcon.svg";
import { CTAButton } from "@/shadcn/ui/button";
import { PageAffiliateWorkshopSchema } from "@/sanity/pages/PageAffiliateWorkshopTemplate";

export default function MissionSection({
  data,
  className,
}: {
  data: PageAffiliateWorkshopSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip py-[7rem] lg:py-[20rem] px-[2rem] lg:px-0",
        className,
      )}
    >
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[38.25rem] sm:w-[55rem] lg:w-[min(180rem,80%)] h-full flex justify-between opacity-50 lg:opacity-100">
        {Array.from({ length: 7 }).map((_, index) => (
          <div
            key={index}
            className={cn(
              "h-full border-l border-l-[#E5E5E5]",
              index >= 5 && "hidden lg:block",
            )}
          />
        ))}
      </div>

      <div className="w-[min(119.875rem,100%)] mx-auto flex flex-col items-start py-[4.5rem] lg:py-[11.25rem] relative bg-white border border-[#E5E5E5] px-[3.75rem] lg:px-[14.25rem]">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/*<ChipSerif text={data.missionChip} />*/}
          <Image
            src={iqicon}
            alt="iqicon"
            className="!w-[8.625rem] mb-[2.5rem]"
          />
        </motion.div>

        {data.missionTitle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext mb-[5rem]"
          >
            <PortableText
              value={data.missionTitle}
              components={richTextComponent}
            />
          </motion.div>
        )}
        {data.missionContent && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start prose-p:text-[2.5rem] lg:prose-p:text-[3rem]"
          >
            <PortableText
              value={data.missionContent}
              components={richTextComponent}
            />
          </motion.div>
        )}
        {data.missionHighlightText && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="my-[5rem] bg-[#E6EDE9] p-[4rem] lg:p-[5.85rem] rounded-[1rem] lg:rounded-[2rem] richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2.5rem] prose-headings:text-[#103037]/80 prose-headings:text-[2.5rem] lg:prose-headings:text-[3rem] prose-headings:font-medium"
          >
            <PortableText
              value={data.missionHighlightText}
              components={richTextComponent}
            />
          </motion.div>
        )}

        {data.missionContent2 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="richtext richtext-start prose-p:text-[2rem] lg:prose-p:text-[2.5rem] prose-headings:text-[3rem] prose-headings:font-semibold prose-headings:mt-0"
          >
            <PortableText
              value={data.missionContent2}
              components={richTextComponent}
            />
          </motion.div>
        )}

        <div className="flex justify-between w-full !mt-[6.5rem]">
          {data.missionSignature && (
            <Image
              src={data.missionSignature}
              alt="signature"
              className="!w-[26rem] lg:!w-[40.5rem]"
            />
          )}
          {data.missionStamp && (
            <Image
              src={data.missionStamp}
              alt="stamp"
              className="lg:absolute lg:bottom-[7rem] lg:right-[7rem] !w-[15rem] lg:!w-[27.5rem]"
            />
          )}
        </div>
      </div>
    </section>
  );
}
